'use client'

import * as React from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'

import { cn } from '@/lib/utils'

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay data-component-id="src/components/ui/dialog.tsx:18:2" data-component-path="src/components/ui/dialog.tsx" data-component-line="18" data-component-end-line="25" data-component-file="dialog.tsx" data-component-name="DialogPrimitive.Overlay" data-component-content="%7B%22elementName%22%3A%22DialogPrimitive.Overlay%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
    ref={ref}
    className={cn(
      'fixed inset-0 z-[300] bg-slate-900/45 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

function isPortaledPopoverTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) return false
  return Boolean(
    target.closest('[data-radix-popover-content]') ||
      target.closest('[data-radix-popper-content-wrapper]'),
  )
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onPointerDownOutside, onInteractOutside, ...props }, ref) => (
  <DialogPortal data-component-id="src/components/ui/dialog.tsx:41:2" data-component-path="src/components/ui/dialog.tsx" data-component-line="41" data-component-end-line="61" data-component-file="dialog.tsx" data-component-name="DialogPortal" data-component-content="%7B%22elementName%22%3A%22DialogPortal%22%7D">
    <DialogOverlay data-component-id="src/components/ui/dialog.tsx:42:4" data-component-path="src/components/ui/dialog.tsx" data-component-line="42" data-component-end-line="42" data-component-file="dialog.tsx" data-component-name="DialogOverlay" data-component-content="%7B%22elementName%22%3A%22DialogOverlay%22%7D" />
    <DialogPrimitive.Content data-component-id="src/components/ui/dialog.tsx:43:4" data-component-path="src/components/ui/dialog.tsx" data-component-line="43" data-component-end-line="60" data-component-file="dialog.tsx" data-component-name="DialogPrimitive.Content" data-component-content="%7B%22elementName%22%3A%22DialogPrimitive.Content%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-[301] flex max-h-[min(92vh,52rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 shadow-xl outline-none duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        className,
      )}
      onPointerDownOutside={(e) => {
        if (isPortaledPopoverTarget(e.target)) e.preventDefault()
        onPointerDownOutside?.(e)
      }}
      onInteractOutside={(e) => {
        if (isPortaledPopoverTarget(e.target)) e.preventDefault()
        onInteractOutside?.(e)
      }}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div data-component-id="src/components/ui/dialog.tsx:67:4" data-component-path="src/components/ui/dialog.tsx" data-component-line="67" data-component-end-line="67" data-component-file="dialog.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" className={cn('shrink-0 flex flex-col gap-1.5 border-b border-slate-100 bg-white px-6 pb-4 pt-6 pr-14 text-left', className)} {...props} />
  )
}

function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div data-component-id="src/components/ui/dialog.tsx:72:9" data-component-path="src/components/ui/dialog.tsx" data-component-line="72" data-component-end-line="72" data-component-file="dialog.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain bg-white', className)} {...props} />
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div data-component-id="src/components/ui/dialog.tsx:77:4" data-component-path="src/components/ui/dialog.tsx" data-component-line="77" data-component-end-line="83" data-component-file="dialog.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      className={cn(
        'shrink-0 flex flex-col-reverse gap-2 border-t border-slate-100 bg-white px-6 py-4 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  )
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title data-component-id="src/components/ui/dialog.tsx:91:2" data-component-path="src/components/ui/dialog.tsx" data-component-line="91" data-component-end-line="95" data-component-file="dialog.tsx" data-component-name="DialogPrimitive.Title" data-component-content="%7B%22elementName%22%3A%22DialogPrimitive.Title%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
    ref={ref}
    className={cn('font-display text-lg font-bold text-ink md:text-xl', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description data-component-id="src/components/ui/dialog.tsx:103:2" data-component-path="src/components/ui/dialog.tsx" data-component-line="103" data-component-end-line="107" data-component-file="dialog.tsx" data-component-name="DialogPrimitive.Description" data-component-content="%7B%22elementName%22%3A%22DialogPrimitive.Description%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
    ref={ref}
    className={cn('text-sm leading-relaxed text-slate-600', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
